import React from "react";
import {
  Container,
  StyledContainer,
  StyledForm,
  StyledFormElementsWrapper,
  FormLabel,
  ButtonGroup,
  BackButton,
  SubmitButton,
  ContainerGrid,
  MainContainer,
} from "./styles";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import { CustomFileUpload } from "../../../common/FormInputs";
import { CustomInputField } from "../../../common/FormInputs";
import { Autocomplete } from "@material-ui/lab";
import { makeStyles, Popper, Grid } from "@material-ui/core";
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import AWSService from "../../../../services/AWSService";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage, getFileName } from "../../../../helpers/functions";
import { splitAndDownloadValidationSchema } from "../../../../helpers/validationSchema";
import { CheckboxContainer } from "../../../common/FormInputs";
import { CustomCheckbox } from "../../../common/FormInputs";
import { CheckBoxLabel } from "../../CreateParty/styles";

const useStyles = makeStyles((theme) => ({
  listbox: {
    color: "#293461",
    fontFamily: "openSans-SemiBold, sans-serif",
    fontSize: "16px",
    backgroundColor: "#ffffff",
    border: "2px solid #8B008B",
  },
  noOptions: {
    fontFamily: "openSans-SemiBold, sans-serif",
    color: "#8294a5",
  },
  customTooltip: {
    backgroundColor: "#D9EAFD",
    color: "#293461",
    fontSize: "12px",
    borderRadius: "8px",
    fontFamily: "openSans-SemiBold, sans-serif",
  },
  customArrow: {
    color: "#D9EAFD",
  },
}));

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;

  // Set dropdown width smaller than input (e.g., 80%)
  const width = anchorEl ? anchorEl.clientWidth * 0.5 : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
      }}
      placement="bottom-start"
    />
  );
};

const SplitAndDownload = ({ menuItems }) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const upload_templates = [
    {
      name: "contractNo",
      label: "Upload Xlsx",
      type: "upload",
      required: true,
      fileName: "excel",
      accept: ".xlsx",
      types: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ],
    },
    {
      name: "pdfFile",
      label: "Pdf File",
      type: "upload",
      required: true,
      fileName: "pdf",
      accept: ".pdf",
      types: ["application/pdf"],
    },
  ];

  async function s3Upload(
    file,
    field,
    setFieldValue,
    fileTypes,
    accept,
    values,
    fileName,
  ) {
    if (file) {
      try {
        const types = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "text/csv",
          "application/pdf",
        ];
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const errorMessage = "Invalid File Type";
        if (!types.includes(file?.type)) throw errorMessage;
        if (accept === ".xlsx") {
          const response = await readExcelFile(file, fileName, setFieldValue);
          setFieldValue(field, response);
          if (response?.length) {
            setFieldValue(fileName, file.name);
          }
        } else if (accept === ".pdf") {
          const payload = {
            key: file.name,
          };
          const s3Response = await AWSService.getS3URL(payload);
          if (s3Response.url) {
            const res = await AWSService.uploadToS3(s3Response.url, file);
            setFieldValue(field, res);
            enqueueSnackbar("Successfully Uploaded", { variant: "success" });
          }
        }
      } catch (err) {
        enqueueSnackbar(err?.message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  /**
   * @description Read xlsx file
   * @param {*} file
   * @returns
   */

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer(); // Convert file to ArrayBuffer
    const workbook = XLSX.read(arrayBuffer, { type: "array" }); // Read the workbook
    const sheetName = workbook.SheetNames[0]; // Get the first sheet
    const sheet = workbook.Sheets[sheetName]; // Access the first sheet
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 }); // Convert to 2D array

    const headerRow = data[0]; // First row is the header
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(header.trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    // // Extract values under "CONTRACT NUMBER" column
    const contractNumbers = data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);

    return contractNumbers;
  };

  /**
   * @description onFormSubmit
   */

  async function onFormSubmit(values, { resetForm }) {
    try {
      setLoader({ state: true, message: `split and upload...` });
      const payload = {
        pdfFile: values.pdfFile,
        contractNo: values.contractNo,
        noticeCategory: values.noticeCategory,
        blankPages: values?.blankPages ? 1 : 0,
      };
      const response = await CaseService.splitDownload(payload);
      if (response?.fileUrl) {
        window.open(response?.fileUrl);
      }
      resetForm();
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  }

  return (
    <MainContainer>
      <Container>
        <Formik
          initialValues={{
            excel: "",
            contractNo: "",
            noticeCategory: "",
            pdfFile: "",
            blankPages: "",
          }}
          validationSchema={splitAndDownloadValidationSchema}
          enableReinitialize
          onSubmit={onFormSubmit}
          validateOnBlur
          validateOnChange
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            handleSubmit,
            setFieldValue,
            setFieldError,
            resetForm,
          }) => (
            <StyledForm>
              <StyledFormElementsWrapper
                style={{ marginBottom: 8, marginTop: 15 }}
              >
                <div style={{ marginTop: "10px" }}>
                  <FormLabel>Notice Category Name</FormLabel>
                  <Autocomplete
                    id="size-small-outlined"
                    size="small"
                    classes={{ noOptions: classes.noOptions }}
                    options={menuItems}
                    groupBy={(option) => option.category}
                    getOptionLabel={(option) => option.name}
                    PopperComponent={CustomPopper}
                    renderInput={(params) => (
                      <div ref={params.InputProps.ref}>
                        <CustomInputField
                          {...params}
                          style={{ width: "50%" }}
                          variant="outlined"
                        />
                      </div>
                    )}
                    renderGroup={(params) => (
                      <StyledContainer>
                        <div className="doc_row">
                          <div>
                            <FormLabel className="category">
                              {params.group}
                            </FormLabel>
                            <div className="name">{params.children}</div>
                          </div>
                        </div>
                      </StyledContainer>
                    )}
                    noOptionsText={
                      <div variant="body2" color="textSecondary">
                        No Document type found
                      </div>
                    }
                    onChange={(event, newValue) =>
                      setFieldValue("noticeCategory", newValue?.name || "")
                    }
                  />
                </div>
              </StyledFormElementsWrapper>
              <StyledFormElementsWrapper
                style={{ marginBottom: 8, marginTop: 15 }}
              >
                <ContainerGrid className="mis-dashboard">
                  {upload_templates.map((upload, index) => (
                    <div style={{ marginTop: 10 }}>
                      <FormLabel>
                        {upload.label} <span className="required-star"> *</span>
                      </FormLabel>
                      <CustomFileUpload
                        error={
                          upload.accept === ".xlsx"
                            ? touched[upload.fileName] &&
                              errors[upload.fileName]
                            : touched[upload.name] && errors[upload.name]
                        }
                        setFile={(file) =>
                          s3Upload(
                            file,
                            upload.name,
                            setFieldValue,
                            upload.types,
                            upload.accept,
                            values,
                            upload.fileName,
                          )
                        }
                        style={{
                          width: "100%",
                        }}
                        accept={upload.accept}
                        file={{
                          name:
                            upload.fileName === "excel"
                              ? values[upload.fileName]
                              : upload.fileName === "pdf"
                                ? getFileName(values[upload.name])
                                : "",
                        }}
                      />
                    </div>
                  ))}
                </ContainerGrid>
                <Grid
                  item
                  xs={8}
                  spacing={2}
                  style={{ marginTop: "15px", paddingRight: 16 }}
                >
                  <CheckboxContainer>
                    <CustomCheckbox
                      name="blankPages"
                      checked={values.blankPages}
                      onChange={handleChange}
                    />
                    <CheckBoxLabel>With Blank Pages</CheckBoxLabel>
                  </CheckboxContainer>
                </Grid>
              </StyledFormElementsWrapper>
              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard")}>
                  Back
                </BackButton>
                <SubmitButton onClick={handleSubmit}>Submit</SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Container>
    </MainContainer>
  );
};

export default SplitAndDownload;
